import React, { FC, memo, useMemo } from 'react';

import Decimal from 'decimal.js';

import Typography from '@mui/material/Typography';

import { getTokenWeights } from '@helpers/vaults/vault.helpers';

import { formatBNWithCommas } from '@utils/formaters.utils';

import SkeletonLoader from '@components/common/SkeletonLoader';
import TokenWeight from '@components/vaults/TokenWeight';

import {
  PRICE_IMPACT_COLOR,
  PriceImpactLevel,
} from '@constants/vaults/vault.constants';

import { CommonTokenDto } from '@dto/common/tokens.dto';

import useStyles from './styles.module';

interface Props {
  isPercentagesChanged: boolean;

  priceImpactPercent: Decimal;
  token0: CommonTokenDto;
  token1: CommonTokenDto;
  isPending: boolean;
  priceImpactLevel: PriceImpactLevel;
  amount0: bigint;
  amount1: bigint;
}

const ProjectedLiquidityPosition: FC<Props> = ({
  isPercentagesChanged,
  token1,
  token0,
  isPending,
  priceImpactLevel,
  priceImpactPercent,
  amount1,
  amount0,
}) => {
  const { classes } = useStyles();

  const projectedWeight = useMemo(
    () =>
      getTokenWeights({
        amount0,
        amount1,
        token0,
        token1,
      }),
    [amount0, amount1, token0, token1],
  );

  return (
    <div className={classes.statsRow}>
      <div className={classes.positionRow}>
        <Typography color={(color) => color.primary.p2} variant="subtitle1">
          Projected liquidity position
        </Typography>

        <TokenWeight
          classNameItem={classes.tokenWeight}
          className={classes.tokensWeight}
          token0Weight={projectedWeight?.token0Weight}
          token1Weight={projectedWeight?.token1Weight}
          symbol0={token0?.symbol}
          symbol1={token1?.symbol}
        />
      </div>

      <div className={classes.priceImpactRow}>
        <Typography
          color={(color) => color.primary.p2}
          className={classes.text}
          variant="subtitle1"
        >
          Est. Price Impact
        </Typography>

        {isPercentagesChanged ? (
          isPending ? (
            <SkeletonLoader className={classes.loader} />
          ) : (
            <Typography
              color={PRICE_IMPACT_COLOR[priceImpactLevel]}
              variant="body1"
            >
              {formatBNWithCommas(priceImpactPercent, 4, 0, false, true)}%
            </Typography>
          )
        ) : (
          '-'
        )}
      </div>
    </div>
  );
};

export default memo(ProjectedLiquidityPosition);
